/**
 * 地区选择器组件
 * 横向排列的省市区街道四级联动选择器
 */

import React, { useState, useEffect } from 'react';
import { Select, Button, Space } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { getStreets } from '../services/api';

const { Option } = Select;

const RegionSelector = ({
  regions,
  onFilterChange,
  initialProvince = null,
  // 新增：受控选择值（用于双击下钻）
  controlledProvince,
  controlledCity,
  controlledDistrict,
  controlledStreet,
}) => {
  const [selectedProvince, setSelectedProvince] = useState(initialProvince);
  const [selectedCity, setSelectedCity] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedStreet, setSelectedStreet] = useState(null);
  // 街道列表（按需加载）
  const [streets, setStreets] = useState([]);
  const [streetsLoading, setStreetsLoading] = useState(false);

  // 同步受控值
  useEffect(() => {
    if (controlledProvince !== undefined) {
      setSelectedProvince(controlledProvince);
    }
  }, [controlledProvince]);

  useEffect(() => {
    if (controlledCity !== undefined) {
      setSelectedCity(controlledCity);
    }
  }, [controlledCity]);

  useEffect(() => {
    if (controlledDistrict !== undefined) {
      setSelectedDistrict(controlledDistrict);
    }
  }, [controlledDistrict]);

  useEffect(() => {
    if (controlledStreet !== undefined) {
      setSelectedStreet(controlledStreet);
    }
  }, [controlledStreet]);

  // 根据已选省份过滤城市
  const getFilteredCities = () => {
    if (!regions.cities || !selectedProvince) {
      return [];
    }
    const citySet = new Set();
    const filtered = regions.cities
      .filter(c => c.pname === selectedProvince)
      .map(c => {
        if (!citySet.has(c.cityname)) {
          citySet.add(c.cityname);
          return c.cityname;
        }
        return null;
      })
      .filter(c => c !== null);

    return filtered.sort((a, b) => a.localeCompare(b, 'zh-CN'));
  };

  // 根据已选省份和城市过滤区县
  const getFilteredDistricts = () => {
    if (!regions.districts || !selectedProvince || !selectedCity) {
      return [];
    }
    const districtSet = new Set();
    const filtered = regions.districts
      .filter(d =>
        d.pname === selectedProvince &&
        d.cityname === selectedCity
      )
      .map(d => {
        if (!districtSet.has(d.adname)) {
          districtSet.add(d.adname);
          return d.adname;
        }
        return null;
      })
      .filter(d => d !== null);

    return filtered.sort((a, b) => a.localeCompare(b, 'zh-CN'));
  };

  // 根据已选省市区过滤街道 - 现在使用按需加载的streets状态
  const getFilteredStreets = () => {
    return streets;
  };

  // 当区县变化时，加载街道列表
  useEffect(() => {
    const loadStreets = async () => {
      if (selectedProvince && selectedCity && selectedDistrict) {
        setStreetsLoading(true);
        try {
          const response = await getStreets(selectedProvince, selectedCity, selectedDistrict);
          setStreets(response.streets || []);
        } catch (error) {
          console.error('加载街道列表失败:', error);
          setStreets([]);
        } finally {
          setStreetsLoading(false);
        }
      } else {
        setStreets([]);
      }
    };
    loadStreets();
  }, [selectedProvince, selectedCity, selectedDistrict]);

  // 当省份变化时，清空下级选择
  useEffect(() => {
    setSelectedCity(null);
    setSelectedDistrict(null);
    setSelectedStreet(null);
  }, [selectedProvince]);

  // 当城市变化时，清空下级选择
  useEffect(() => {
    setSelectedDistrict(null);
    setSelectedStreet(null);
  }, [selectedCity]);

  // 当区县变化时，清空街道选择
  useEffect(() => {
    setSelectedStreet(null);
  }, [selectedDistrict]);

  // 重置筛选
  const handleReset = () => {
    setSelectedProvince(null);
    setSelectedCity(null);
    setSelectedDistrict(null);
    setSelectedStreet(null);
    onFilterChange({});
  };

  // 省份变化时自动触发搜索
  const handleProvinceChange = (value) => {
    setSelectedProvince(value);
    // 无论选择还是清除，都触发筛选更新
    setTimeout(() => {
      if (value) {
        onFilterChange({
          province: value,
          city: null,
          district: null,
          street: null,
        });
      } else {
        // 清除省份时，清空所有筛选
        onFilterChange({});
      }
    }, 0);
  };

  // 城市变化时自动触发搜索
  const handleCityChange = (value) => {
    setSelectedCity(value);
    // 无论选择还是清除，都触发筛选更新
    setTimeout(() => {
      onFilterChange({
        province: selectedProvince,
        city: value || null,
        district: null,
        street: null,
      });
    }, 0);
  };

  // 区县变化时自动触发搜索
  const handleDistrictChange = (value) => {
    setSelectedDistrict(value);
    // 无论选择还是清除，都触发筛选更新
    setTimeout(() => {
      onFilterChange({
        province: selectedProvince,
        city: selectedCity,
        district: value || null,
        street: null,
      });
    }, 0);
  };

  // 街道变化时自动触发搜索
  const handleStreetChange = (value) => {
    setSelectedStreet(value);
    // 无论选择还是清除，都触发筛选更新
    setTimeout(() => {
      onFilterChange({
        province: selectedProvince,
        city: selectedCity,
        district: selectedDistrict,
        street: value || null,
      });
    }, 0);
  };

  return (
    <div style={styles.container}>
      <Space size="middle" wrap>
        {/* 省份选择 */}
        <div style={styles.selectWrapper}>
          <span style={styles.label}>省份:</span>
          <Select
            style={styles.select}
            placeholder="请选择省份"
            value={selectedProvince}
            onChange={handleProvinceChange}
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {regions.provinces?.sort((a, b) => a.localeCompare(b, 'zh-CN')).map(province => (
              <Option key={province} value={province}>
                {province}
              </Option>
            ))}
          </Select>
        </div>

        {/* 城市选择 */}
        <div style={styles.selectWrapper}>
          <span style={styles.label}>城市:</span>
          <Select
            style={styles.select}
            placeholder="请选择城市"
            value={selectedCity}
            onChange={handleCityChange}
            disabled={!selectedProvince}
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {getFilteredCities().map(city => (
              <Option key={city} value={city}>
                {city}
              </Option>
            ))}
          </Select>
        </div>

        {/* 区县选择 */}
        <div style={styles.selectWrapper}>
          <span style={styles.label}>区县:</span>
          <Select
            style={styles.select}
            placeholder="请选择区县"
            value={selectedDistrict}
            onChange={handleDistrictChange}
            disabled={!selectedCity}
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {getFilteredDistricts().map(district => (
              <Option key={district} value={district}>
                {district}
              </Option>
            ))}
          </Select>
        </div>

        {/* 街道选择 */}
        <div style={styles.selectWrapper}>
          <span style={styles.label}>街道:</span>
          <Select
            style={styles.selectWide}
            placeholder={streetsLoading ? "加载中..." : "请选择街道"}
            value={selectedStreet}
            onChange={handleStreetChange}
            disabled={!selectedDistrict}
            loading={streetsLoading}
            allowClear
            showSearch
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {getFilteredStreets().map(street => (
              <Option key={street} value={street}>
                {street}
              </Option>
            ))}
          </Select>
        </div>

        {/* 重置按钮 */}
        <Button
          icon={<ReloadOutlined />}
          onClick={handleReset}
        >
          重置
        </Button>
      </Space>
    </div>
  );
};

const styles = {
  container: {
    padding: '12px 24px',
    backgroundColor: '#fff',
    borderBottom: '1px solid #e8e8e8',
    boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
  },
  selectWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  label: {
    color: '#666',
    fontSize: '14px',
    whiteSpace: 'nowrap',
  },
  select: {
    width: '140px',
  },
  selectWide: {
    width: '180px',
  },
};

export default RegionSelector;
